import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Spinner} from 'react-bootstrap'
import toaster from '../../../../Utils/toaster'
import {
  BulkVosSettingsDto,
  fetchBulkVosSettings,
  saveBulkVosSettings,
} from '../../bulk-vos/bulkVosSettingsApi'
import {
  formatSummaryRaw,
  formatSummarySentence,
  normalizeScheduleTime,
} from '../../bulk-vos/bulkVosSettingsFormat'
import '../../bulk-vos/bulkVosSettings.css'

const DAY_BITS = [0, 1, 2, 3, 4, 5, 6]
const DAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

function defaultSettings(): BulkVosSettingsDto {
  return {
    isEnabled: false,
    recipients: '',
    scheduleTimeLocal: '10:30',
    repeatInterval: 2,
    repeatUnit: 1,
    repeatDaysOfWeek: 1 << 5,
    repeatDayOfMonth: 1,
    endsType: 0,
    endsAfterOccurrences: 13,
    includeManualVos: true,
    includeDigitalVos: true,
    includeIpi: true,
    includeVis: true,
  }
}

function readSettingBool(
  data: Record<string, unknown>,
  camel: string,
  pascal: string,
  fallback: boolean
): boolean {
  const raw = data[camel] ?? data[pascal]
  if (raw === true || raw === false) return raw
  if (raw === 1 || raw === '1' || raw === 'true') return true
  if (raw === 0 || raw === '0' || raw === 'false') return false
  if (raw == null) return fallback
  return Boolean(raw)
}

function normalizeLoaded(data: BulkVosSettingsDto): BulkVosSettingsDto {
  const raw = data as BulkVosSettingsDto & Record<string, unknown>
  const defaults = defaultSettings()
  const scheduleRaw =
    (raw.scheduleTimeLocal as string | undefined) ??
    (raw.ScheduleTimeLocal as string | undefined)
  const endsOnRaw =
    (raw.endsOnDate as string | undefined) ?? (raw.EndsOnDate as string | undefined)

  return {
    ...defaults,
    ...data,
    isEnabled: readSettingBool(raw, 'isEnabled', 'IsEnabled', defaults.isEnabled),
    includeManualVos: readSettingBool(
      raw,
      'includeManualVos',
      'IncludeManualVos',
      defaults.includeManualVos
    ),
    includeDigitalVos: readSettingBool(
      raw,
      'includeDigitalVos',
      'IncludeDigitalVos',
      defaults.includeDigitalVos
    ),
    includeIpi: readSettingBool(raw, 'includeIpi', 'IncludeIpi', defaults.includeIpi),
    includeVis: readSettingBool(raw, 'includeVis', 'IncludeVis', defaults.includeVis),
    scheduleTimeLocal: normalizeScheduleTime(scheduleRaw),
    endsOnDate: endsOnRaw ? String(endsOnRaw).substring(0, 10) : undefined,
    endsAfterOccurrences:
      data.endsAfterOccurrences ??
      (raw.EndsAfterOccurrences as number | undefined) ??
      (data.endsType === 2 ? 13 : defaults.endsAfterOccurrences),
    repeatDayOfMonth:
      data.repeatDayOfMonth ??
      (raw.RepeatDayOfMonth as number | undefined) ??
      defaults.repeatDayOfMonth,
  }
}

export function BulkVosSettingsTab({customerId}: {customerId: number}) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState<BulkVosSettingsDto>(defaultSettings())
  const [form, setForm] = useState<BulkVosSettingsDto>(defaultSettings())

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchBulkVosSettings(customerId)
      const merged = normalizeLoaded(data)
      setSaved(merged)
      setForm(merged)
    } catch {
      toaster('error', 'Failed to load bulk VOS settings')
    } finally {
      setLoading(false)
    }
  }, [customerId])

  useEffect(() => {
    load()
  }, [load])

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(saved), [form, saved])
  const vosTypeError = !form.includeManualVos && !form.includeDigitalVos
  const commTypeError = !form.includeIpi && !form.includeVis
  const monthDayInvalid =
    form.repeatUnit === 2 &&
    (form.repeatDayOfMonth == null ||
      form.repeatDayOfMonth < 1 ||
      form.repeatDayOfMonth > 31)
  const blocked = vosTypeError || commTypeError || monthDayInvalid
  const hasNoRecipients = !(form.recipients ?? '').trim()

  const summarySentence = useMemo(() => formatSummarySentence(form), [form])
  const summaryRaw = useMemo(() => formatSummaryRaw(form), [form])

  const patch = (patchForm: Partial<BulkVosSettingsDto>) => {
    setForm((f) => ({...f, ...patchForm}))
  }

  const toggleDay = (bit: number) => {
    setForm((f) => ({
      ...f,
      repeatDaysOfWeek: (f.repeatDaysOfWeek ^ (1 << bit)) >>> 0,
    }))
  }

  const save = async () => {
    if (blocked) return
    setSaving(true)
    try {
      const payload = {
        ...form,
        scheduleSummary: formatSummaryRaw(form),
        endsAfterOccurrences:
          form.endsType === 2 ? form.endsAfterOccurrences ?? 13 : form.endsAfterOccurrences,
      }
      await saveBulkVosSettings(customerId, payload)
      setSaved(form)
      toaster('success', 'Settings saved')
    } catch {
      toaster('error', 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className='p-5 text-center'>
        <Spinner animation='border' />
      </div>
    )
  }

  return (
    <div className='bulk-vos-settings-inner'>
      <div>
        <h1 className='bulk-vos-settings-title'>Bulk VOS Approval Email</h1>
        <p className='bulk-vos-settings-subtitle'>
          Configure settings to send all pending VOS for client approval.
        </p>
      </div>

      <div
        className={`bulk-vos-settings-master ${form.isEnabled ? 'bulk-vos-settings-master--on' : ''}`}
      >
        <div className='d-flex align-items-start gap-3 flex-grow-1' style={{minWidth: 260}}>
          <span
            className={`bulk-vos-settings-master-icon ${form.isEnabled ? 'bulk-vos-settings-master-icon--on' : ''}`}
          >
            <i className='bi bi-envelope-check' style={{fontSize: 18}} aria-hidden='true' />
          </span>
          <div>
            <div className='bulk-vos-settings-master-title'>Send Email For Bulk VOS Approvals</div>
            <div className='bulk-vos-settings-master-hint'>
              {form.isEnabled
                ? 'On — pending VOS are emailed to the recipients below on the schedule you set.'
                : 'Off — no approval emails are sent. Turn on to configure the schedule and filters.'}
            </div>
          </div>
        </div>
        <button
          type='button'
          role='switch'
          aria-checked={form.isEnabled}
          className={`bulk-vos-settings-switch ${form.isEnabled ? 'bulk-vos-settings-switch--on' : ''}`}
          onClick={() => patch({isEnabled: !form.isEnabled})}
        >
          <span className='bulk-vos-settings-switch-knob' />
        </button>
      </div>

      <div
        className={`d-flex flex-column gap-3 ${form.isEnabled ? '' : 'bulk-vos-settings-body--disabled'}`}
      >
        <section className='bulk-vos-settings-section'>
          <header className='bulk-vos-settings-section-header'>
            <i className='bi bi-clock-history' style={{fontSize: 15, color: '#7e8299'}} aria-hidden='true' />
            <h2>Schedule</h2>
            <span className='bulk-vos-settings-section-header-meta' />
            <span className='bulk-vos-settings-section-header-aside'>Client local time</span>
          </header>
          <div className='bulk-vos-settings-section-body'>
            <div className='bulk-vos-settings-field-row'>
              <span className='bulk-vos-settings-field-muted'>Send at</span>
              <input
                type='time'
                className='bulk-vos-settings-input bulk-vos-settings-input--time'
                value={form.scheduleTimeLocal ?? '10:30'}
                onChange={(e) => patch({scheduleTimeLocal: e.target.value})}
              />
              <span className='bulk-vos-settings-field-muted ms-1'>repeat every</span>
              <input
                type='number'
                min={1}
                className='bulk-vos-settings-input bulk-vos-settings-input--num-sm'
                value={form.repeatInterval}
                onChange={(e) => patch({repeatInterval: Number(e.target.value) || 1})}
              />
              <select
                className='bulk-vos-settings-input'
                value={form.repeatUnit}
                onChange={(e) => {
                  const repeatUnit = Number(e.target.value)
                  const next: Partial<BulkVosSettingsDto> = {repeatUnit}
                  if (repeatUnit === 2 && (form.repeatDayOfMonth == null || form.repeatDayOfMonth < 1))
                    next.repeatDayOfMonth = 1
                  patch(next)
                }}
              >
                <option value={0}>day</option>
                <option value={1}>week</option>
                <option value={2}>month</option>
              </select>
            </div>

            {form.repeatUnit === 2 && (
              <div className='bulk-vos-settings-field-row'>
                <span className='bulk-vos-settings-label-sm'>On Day</span>
                <input
                  type='number'
                  min={1}
                  max={31}
                  className='bulk-vos-settings-input bulk-vos-settings-input--num-sm'
                  value={form.repeatDayOfMonth ?? 1}
                  onChange={(e) => {
                    const n = Number(e.target.value)
                    patch({repeatDayOfMonth: Number.isFinite(n) ? n : 1})
                  }}
                />
              </div>
            )}

            {form.repeatUnit === 1 && (
              <div className='d-flex flex-column gap-2'>
                <span className='bulk-vos-settings-label-sm'>Repeat on</span>
                <div className='d-flex gap-2 flex-wrap'>
                  {DAY_LETTERS.map((label, idx) => {
                    const on = (form.repeatDaysOfWeek & (1 << DAY_BITS[idx])) !== 0
                    return (
                      <button
                        key={`${label}-${idx}`}
                        type='button'
                        title={
                          [
                            'Sunday',
                            'Monday',
                            'Tuesday',
                            'Wednesday',
                            'Thursday',
                            'Friday',
                            'Saturday',
                          ][idx]
                        }
                        className={`bulk-vos-settings-day-pill ${on ? 'bulk-vos-settings-day-pill--on' : ''}`}
                        onClick={() => toggleDay(DAY_BITS[idx])}
                      >
                        {label}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            <div className='d-flex flex-column gap-2'>
              <span className='bulk-vos-settings-label-sm'>Ends</span>
              <div className='d-flex flex-wrap gap-2'>
                <button
                  type='button'
                  className={`bulk-vos-settings-segment ${form.endsType === 0 ? 'bulk-vos-settings-segment--on' : ''}`}
                  onClick={() => patch({endsType: 0})}
                >
                  Never
                </button>
                <button
                  type='button'
                  className={`bulk-vos-settings-segment ${form.endsType === 1 ? 'bulk-vos-settings-segment--on' : ''}`}
                  onClick={() => patch({endsType: 1})}
                >
                  On a date
                </button>
                <button
                  type='button'
                  className={`bulk-vos-settings-segment ${form.endsType === 2 ? 'bulk-vos-settings-segment--on' : ''}`}
                  onClick={() =>
                    patch({
                      endsType: 2,
                      endsAfterOccurrences: form.endsAfterOccurrences ?? 13,
                    })
                  }
                >
                  After occurrences
                </button>
              </div>
              {form.endsType === 1 && (
                <input
                  type='date'
                  className='bulk-vos-settings-input bulk-vos-settings-input--date'
                  value={form.endsOnDate ?? ''}
                  onChange={(e) => patch({endsOnDate: e.target.value})}
                />
              )}
              {form.endsType === 2 && (
                <div className='bulk-vos-settings-field-row'>
                  <input
                    type='number'
                    min={1}
                    className='bulk-vos-settings-input bulk-vos-settings-input--num-md'
                    value={form.endsAfterOccurrences ?? 13}
                    onChange={(e) =>
                      patch({endsAfterOccurrences: Number(e.target.value) || 1})
                    }
                  />
                  <span className='bulk-vos-settings-field-muted'>occurrences</span>
                </div>
              )}
            </div>

            <div className='bulk-vos-settings-summary'>
              <i className='bi bi-calendar-check' style={{fontSize: 14, color: '#a3a242', marginTop: 1}} />
              <div>
                <div className='bulk-vos-settings-summary-title'>{summarySentence}</div>
                <div className='bulk-vos-settings-summary-raw'>{summaryRaw}</div>
              </div>
            </div>
          </div>
        </section>

        <section className='bulk-vos-settings-section'>
          <header className='bulk-vos-settings-section-header'>
            <i className='bi bi-people' style={{fontSize: 15, color: '#7e8299'}} aria-hidden='true' />
            <h2>Recipients</h2>
          </header>
          <div className='bulk-vos-settings-section-body gap-2'>
            <input
              type='text'
              className='bulk-vos-settings-input bulk-vos-settings-input--full'
              placeholder='name@company.com, finance@company.com'
              value={form.recipients ?? ''}
              onChange={(e) => patch({recipients: e.target.value})}
            />
            <div className='bulk-vos-settings-recipients-hint'>
              Comma separated. Each recipient gets a secure link that approves the whole batch.
            </div>
            {hasNoRecipients && (
              <div className='bulk-vos-settings-warning'>
                <i className='bi bi-exclamation-circle' aria-hidden='true' />
                Add at least one recipient or the scheduled email will not send.
              </div>
            )}
          </div>
        </section>

        <section className='bulk-vos-settings-section'>
          <header className='bulk-vos-settings-section-header'>
            <i className='bi bi-sliders' style={{fontSize: 15, color: '#7e8299'}} aria-hidden='true' />
            <h2>Batch filters</h2>
            <span className='bulk-vos-settings-section-header-meta' />
            <span className='bulk-vos-settings-section-header-aside'>
              Which pending VOS records go into the batch
            </span>
          </header>
          <div className='bulk-vos-settings-section-body'>
            <div className='bulk-vos-settings-filters-grid'>
              <div className='d-flex flex-column gap-2'>
                <span className='bulk-vos-settings-label-sm'>VOS Type</span>
                <div className='d-flex flex-wrap gap-4'>
                  <div className='mb-0'>
                    <input
                      className='form-check-input me-2'
                      type='checkbox'
                      id='bulkVosIncludeManual'
                      checked={!!form.includeManualVos}
                      onChange={(e) => patch({includeManualVos: e.target.checked})}
                    />
                    <label className='form-check-label' htmlFor='bulkVosIncludeManual'>
                      Manual
                    </label>
                  </div>
                  <div className='mb-0'>
                    <input
                      className='form-check-input me-2'
                      type='checkbox'
                      id='bulkVosIncludeDigital'
                      checked={!!form.includeDigitalVos}
                      onChange={(e) => patch({includeDigitalVos: e.target.checked})}
                    />
                    <label className='form-check-label' htmlFor='bulkVosIncludeDigital'>
                      Digital
                    </label>
                  </div>
                </div>
                {vosTypeError && (
                  <span className='bulk-vos-settings-error'>Select at least one VOS type.</span>
                )}
              </div>
              <div className='d-flex flex-column gap-2'>
                <span className='bulk-vos-settings-label-sm'>Communication Type</span>
                <div className='d-flex flex-wrap gap-4'>
                  <div className='mb-0'>
                    <input
                      className='form-check-input me-2'
                      type='checkbox'
                      id='bulkVosIncludeIpi'
                      checked={!!form.includeIpi}
                      onChange={(e) => patch({includeIpi: e.target.checked})}
                    />
                    <label className='form-check-label' htmlFor='bulkVosIncludeIpi'>
                      IPI
                    </label>
                  </div>
                  <div className='mb-0'>
                    <input
                      className='form-check-input me-2'
                      type='checkbox'
                      id='bulkVosIncludeVis'
                      checked={!!form.includeVis}
                      onChange={(e) => patch({includeVis: e.target.checked})}
                    />
                    <label className='form-check-label' htmlFor='bulkVosIncludeVis'>
                      VIS
                    </label>
                  </div>
                </div>
                {commTypeError && (
                  <span className='bulk-vos-settings-error'>Select at least one communication type.</span>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className='bulk-vos-settings-actions'>
        <span className='bulk-vos-settings-actions-label'>
          {blocked
            ? 'Fix the highlighted filters before saving.'
            : dirty
              ? 'Unsaved changes'
              : 'All changes saved'}
        </span>
        <button
          type='button'
          className='bulk-vos-settings-btn-discard'
          disabled={!dirty || saving}
          onClick={() => setForm(saved)}
        >
          Discard changes
        </button>
        <button
          type='button'
          className='bulk-vos-settings-btn-save'
          disabled={!dirty || blocked || saving}
          onClick={save}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  )
}
